import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { supabase } from '@/integrations/supabase/client';
import SafeIcon from '../components/SafeIcon';
import { 
  getFeatureFlagsForUser, 
  canPerformAiAnalysis,
  getPlanDisplayInfo,
  getEffectivePlanId,
  isInActiveTrial,
  getTrialDaysRemaining
} from '../services/featureFlagsService';
import * as FiIcons from 'react-icons/fi';

const { 
  FiShield, FiAlertTriangle, FiRefreshCw, FiLoader, FiCheck, FiX, 
  FiUser, FiActivity, FiCpu, FiLock, FiUnlock, FiInfo, FiChevronDown
} = FiIcons;

const PlanDebugger = () => {
  const { user } = useAuth();
  const { isAdmin, loading: authLoading } = useSubscription();
  
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all users with plans
  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: rpcError } = await supabase.rpc('admin_get_users_with_plans');
      
      if (rpcError) throw rpcError;
      
      if (data?.success && data?.users) {
        setUsers(data.users);
        console.log('✅ Users loaded for debugger:', data.users.length);
      } else {
        throw new Error(data?.error || 'Failed to load users');
      }
    } catch (err) {
      console.error('❌ Error fetching users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchUsers();
    }
  }, [isAdmin, authLoading]);

  // When a user is selected, compute their feature flags
  useEffect(() => {
    if (selectedUserId) {
      const foundUser = users.find(u => u.user_id === selectedUserId);
      setSelectedUser(foundUser || null);
    } else {
      setSelectedUser(null);
    }
  }, [selectedUserId, users]);

  // Compute feature flags for selected user
  const userForFlags = useMemo(() => {
    if (!selectedUser) return null;
    return {
      subscription_plan_id: selectedUser.subscription_plan_id,
      analyses_used_this_month: selectedUser.analyses_used_this_month || 0,
      role: selectedUser.role,
      is_in_trial: selectedUser.is_in_trial,
      trial_ends_at: selectedUser.trial_ends_at
    };
  }, [selectedUser]);

  const featureFlags = useMemo(() => {
    if (!userForFlags) return null;
    return getFeatureFlagsForUser(userForFlags);
  }, [userForFlags]);

  const analysisCheck = useMemo(() => {
    if (!userForFlags) return null;
    return canPerformAiAnalysis(userForFlags);
  }, [userForFlags]);

  const effectivePlan = useMemo(() => {
    if (!userForFlags) return null;
    return getEffectivePlanId(userForFlags);
  }, [userForFlags]);

  const planInfo = useMemo(() => {
    return getPlanDisplayInfo(effectivePlan);
  }, [effectivePlan]);

  const trialInfo = useMemo(() => {
    if (!userForFlags) return null;
    return {
      isInTrial: isInActiveTrial(userForFlags),
      daysRemaining: getTrialDaysRemaining(userForFlags),
      trialStartedAt: selectedUser?.trial_started_at,
      trialEndsAt: selectedUser?.trial_ends_at
    };
  }, [userForFlags, selectedUser]);

  // Loading state
  if (authLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-900 text-white">
        <SafeIcon icon={FiLoader} className="animate-spin text-blue-500 text-5xl mb-4" />
        <p className="text-gray-400">Loading...</p>
      </div>
    );
  }

  // Admin verification
  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-900 text-white p-8">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-8 max-w-2xl">
          <SafeIcon icon={FiAlertTriangle} className="text-red-400 text-6xl mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-red-300 mb-4 text-center">Admin Access Required</h1>
          <p className="text-gray-400 text-center">This page is only accessible to administrators.</p>
        </div>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  // Group flags by category for display
  const flagCategories = featureFlags ? {
    'Core Access': {
      canUseAiAnalysis: featureFlags.canUseAiAnalysis,
      canUseJournal: featureFlags.canUseJournal,
      aiAnalysesLimit: featureFlags.aiAnalysesLimit,
    },
    'Basic Features': {
      canSeeBasicNoTrade: featureFlags.canSeeBasicNoTrade,
      hasStructuredNoTradeLogic: featureFlags.hasStructuredNoTradeLogic,
    },
    'Tracking & Metrics': {
      hasCounterfactualTracking: featureFlags.hasCounterfactualTracking,
      hasDecisionQualityScore: featureFlags.hasDecisionQualityScore,
      hasRiskAvoidanceScore: featureFlags.hasRiskAvoidanceScore,
      hasCapitalPreservationPercent: featureFlags.hasCapitalPreservationPercent,
      hasImpulsiveTraderBenchmark: featureFlags.hasImpulsiveTraderBenchmark,
    },
    'TDI Score': {
      hasTdiScore: featureFlags.hasTdiScore,
      hasTdiHistoryChart: featureFlags.hasTdiHistoryChart,
      hasBehavioralDriftDetection: featureFlags.hasBehavioralDriftDetection,
      hasOvertradingClusterAnalysis: featureFlags.hasOvertradingClusterAnalysis,
    },
    'Institutional Features': {
      hasInstitutionalReviewBlock: featureFlags.hasInstitutionalReviewBlock,
      hasRiskOfficerStance: featureFlags.hasRiskOfficerStance,
      hasCapitalProtectionScore: featureFlags.hasCapitalProtectionScore,
      hasMarketAlignmentScore: featureFlags.hasMarketAlignmentScore,
      hasDisciplineAssessment: featureFlags.hasDisciplineAssessment,
      hasFullCounterfactualProofEngine: featureFlags.hasFullCounterfactualProofEngine,
    },
    'Reporting': {
      hasAuditPdf: featureFlags.hasAuditPdf,
      hasPmReadyReporting: featureFlags.hasPmReadyReporting,
    },
    'Enterprise Only': {
      hasComplianceFeatures: featureFlags.hasComplianceFeatures,
      hasAuditApi: featureFlags.hasAuditApi,
      hasMultiUserDashboards: featureFlags.hasMultiUserDashboards,
      hasDisciplineReportsDailyWeekly: featureFlags.hasDisciplineReportsDailyWeekly,
      hasWhiteLabelAi: featureFlags.hasWhiteLabelAi,
      hasCustomRiskOfficerProfiles: featureFlags.hasCustomRiskOfficerProfiles,
      hasPrioritySupport: featureFlags.hasPrioritySupport,
    },
  } : null;

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 sm:p-6 lg:p-8">
      <motion.div 
        className="max-w-6xl mx-auto"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        {/* Header */}
        <motion.div variants={itemVariants} className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center">
              <div className="p-2 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 mr-3">
                <SafeIcon icon={FiCpu} className="text-cyan-400"/>
              </div>
              Feature Flags Debugger
            </h1>
            <p className="text-gray-400 mt-2">Inspect subscription plans and feature access for any user</p>
          </div>
          <button
            onClick={fetchUsers}
            disabled={loading}
            className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white font-bold py-2 px-4 rounded-xl flex items-center transition-all disabled:opacity-50"
          >
            <SafeIcon icon={FiRefreshCw} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </motion.div>

        {/* Admin Warning */}
        <motion.div variants={itemVariants} className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6 flex items-start">
          <SafeIcon icon={FiAlertTriangle} className="text-amber-400 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-amber-300">Admin-Only Page</h3>
            <p className="text-amber-200/70 text-sm">This debugger shows feature flags and subscription details. Only visible to administrators.</p>
          </div>
        </motion.div>

        {error && (
          <motion.div variants={itemVariants} className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
            <p className="text-red-300">{error}</p>
          </motion.div>
        )}

        {/* User Selector */}
        <motion.div variants={itemVariants} className="bg-gray-800/50 border border-purple-500/20 rounded-xl p-6 mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-3">
            Select User to Inspect
          </label>
          <div className="relative">
            <select
              value={selectedUserId || ''}
              onChange={(e) => setSelectedUserId(e.target.value || null)}
              className="w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none cursor-pointer"
            >
              <option value="">-- Select a user --</option>
              {users.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {u.email} ({u.plan?.name || 'No Plan'}) - {u.analyses_used_this_month || 0} analyses
                </option>
              ))}
            </select>
            <SafeIcon icon={FiChevronDown} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          </div>
        </motion.div>

        {/* Selected User Details */}
        {selectedUser && (
          <motion.div 
            variants={itemVariants}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* User Info Card */}
            <div className="bg-gray-800/50 border border-purple-500/20 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold flex items-center">
                  <SafeIcon icon={FiUser} className="mr-2 text-cyan-400" />
                  User Information
                </h2>
                {planInfo && (
                  <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                    planInfo.tier === 'enterprise' ? 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20 border border-amber-500/30 text-amber-300' :
                    planInfo.tier === 'institutional' ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-purple-300' :
                    planInfo.tier === 'professional' ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 text-cyan-300' :
                    planInfo.tier === 'starter' ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 text-green-300' :
                    'bg-gray-700/50 border border-gray-600 text-gray-400'
                  }`}>
                    {planInfo.name}
                  </span>
                )}
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gray-900/50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase">Email</p>
                  <p className="text-white font-mono text-sm">{selectedUser.email}</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase">Real Plan ID</p>
                  <p className="text-white font-mono text-sm">{selectedUser.subscription_plan_id || 'null'}</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase">Effective Plan</p>
                  <p className={`font-semibold text-sm ${effectivePlan === 'trial_pro_trader' ? 'text-cyan-400' : 'text-white'}`}>
                    {effectivePlan || 'none'}
                  </p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase">Status</p>
                  <p className={`font-semibold text-sm ${
                    selectedUser.subscription_status === 'active' ? 'text-green-400' : 
                    selectedUser.subscription_status === 'trialing' ? 'text-cyan-400' :
                    selectedUser.subscription_status === 'trial_expired' ? 'text-amber-400' :
                    'text-gray-400'
                  }`}>
                    {selectedUser.subscription_status || 'N/A'}
                  </p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase">Analyses This Month</p>
                  <p className="text-white font-mono text-sm">
                    {selectedUser.analyses_used_this_month || 0}
                    {featureFlags?.aiAnalysesLimit && (
                      <span className="text-gray-500"> / {featureFlags.aiAnalysesLimit}</span>
                    )}
                    {!featureFlags?.aiAnalysesLimit && featureFlags?.canUseAiAnalysis && (
                      <span className="text-cyan-400 ml-1">(Unlimited)</span>
                    )}
                  </p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase">Plan Price</p>
                  <p className="text-white text-sm">
                    {selectedUser.plan?.is_custom_pricing ? 'Custom' : 
                     selectedUser.plan?.price_usd_month ? `$${selectedUser.plan.price_usd_month}/mo` : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Trial Information */}
              {trialInfo && (
                <div className="mt-4 p-4 rounded-lg bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20">
                  <h3 className="text-sm font-semibold text-cyan-300 mb-3 uppercase tracking-wide">Trial Information</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">In Active Trial</p>
                      <p className={`font-semibold text-sm ${trialInfo.isInTrial ? 'text-green-400' : 'text-gray-500'}`}>
                        {trialInfo.isInTrial ? 'Yes' : 'No'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Days Remaining</p>
                      <p className={`font-semibold text-sm ${
                        trialInfo.daysRemaining === null ? 'text-gray-500' :
                        trialInfo.daysRemaining <= 7 ? 'text-amber-400' : 'text-cyan-400'
                      }`}>
                        {trialInfo.daysRemaining !== null ? trialInfo.daysRemaining : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Trial Started</p>
                      <p className="text-white text-xs font-mono">
                        {trialInfo.trialStartedAt ? new Date(trialInfo.trialStartedAt).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Trial Ends</p>
                      <p className="text-white text-xs font-mono">
                        {trialInfo.trialEndsAt ? new Date(trialInfo.trialEndsAt).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Analysis Check Status */}
            {analysisCheck && (
              <div className={`rounded-xl p-6 border ${analysisCheck.allowed ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <SafeIcon 
                      icon={analysisCheck.allowed ? FiUnlock : FiLock} 
                      className={`mr-3 text-2xl ${analysisCheck.allowed ? 'text-green-400' : 'text-red-400'}`} 
                    />
                    <div>
                      <h3 className={`font-bold ${analysisCheck.allowed ? 'text-green-300' : 'text-red-300'}`}>
                        AI Analysis: {analysisCheck.allowed ? 'ALLOWED' : 'BLOCKED'}
                      </h3>
                      {analysisCheck.reason && (
                        <p className="text-sm text-gray-400">{analysisCheck.reason}</p>
                      )}
                    </div>
                  </div>
                  {analysisCheck.remaining !== undefined && analysisCheck.remaining !== null && (
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Remaining</p>
                      <p className={`text-2xl font-bold ${analysisCheck.remaining <= 2 ? 'text-amber-400' : 'text-green-400'}`}>
                        {analysisCheck.remaining}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Feature Flags by Category */}
            {flagCategories && (
              <div className="bg-gray-800/50 border border-purple-500/20 rounded-xl p-6">
                <h2 className="text-xl font-bold flex items-center mb-6">
                  <SafeIcon icon={FiActivity} className="mr-2 text-cyan-400" />
                  Feature Flags
                </h2>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {Object.entries(flagCategories).map(([category, flags]) => (
                    <div key={category} className="bg-gray-900/50 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-purple-300 mb-3 uppercase tracking-wide">{category}</h3>
                      <div className="space-y-2">
                        {Object.entries(flags).map(([key, value]) => (
                          <div key={key} className="flex items-center justify-between text-sm">
                            <span className="text-gray-400 font-mono">{key}</span>
                            {typeof value === 'boolean' ? (
                              <span className={`flex items-center ${value ? 'text-green-400' : 'text-gray-600'}`}>
                                <SafeIcon icon={value ? FiCheck : FiX} className="mr-1" />
                                {value ? 'true' : 'false'}
                              </span>
                            ) : (
                              <span className={`font-semibold ${value === null ? 'text-cyan-400' : 'text-amber-400'}`}>
                                {value === null ? '∞ (unlimited)' : value}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Raw JSON View */}
            <div className="bg-gray-800/50 border border-purple-500/20 rounded-xl p-6">
              <h2 className="text-xl font-bold flex items-center mb-4">
                <SafeIcon icon={FiInfo} className="mr-2 text-cyan-400" />
                Raw Feature Flags (JSON)
              </h2>
              <pre className="bg-gray-900 rounded-lg p-4 overflow-x-auto text-xs text-gray-300 font-mono">
                {JSON.stringify(featureFlags, null, 2)}
              </pre>
            </div>
          </motion.div>
        )}

        {/* No User Selected */}
        {!selectedUser && !loading && (
          <motion.div variants={itemVariants} className="bg-gray-800/30 border border-gray-700 rounded-xl p-12 text-center">
            <SafeIcon icon={FiUser} className="text-gray-600 text-5xl mx-auto mb-4" />
            <p className="text-gray-500">Select a user from the dropdown to view their feature flags and subscription details.</p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default PlanDebugger;
